import React, { createContext, useState, useContext, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, where, getDocs, limitToLast, limit, addDoc, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { firestore } from '../firebase';
import { useAuth } from './AuthContext';

// Default auto-dismiss timeout (8 seconds)
export const AUTO_DISMISS_TIMEOUT = 8000;

// Create the context
const NotificationsContext = createContext();

// Export the context provider
export const NotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMentionsCount, setUnreadMentionsCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadWorkspaceMessages, setUnreadWorkspaceMessages] = useState({});
  const [lastReadChat, setLastReadChat] = useState(() => {
    return localStorage.getItem('lastReadChat') 
      ? new Date(localStorage.getItem('lastReadChat')) 
      : new Date(0);
  });
  const [lastReadWorkspaces, setLastReadWorkspaces] = useState(() => {
    const saved = localStorage.getItem('lastReadWorkspaces');
    return saved ? JSON.parse(saved) : {};
  });
  
  const { currentUser } = useAuth();
  
  // Track mentions and update state
  useEffect(() => {
    if (!currentUser) return;
    
    const q = query(
      collection(firestore, 'mentions'),
      where('userId', '==', currentUser.uid),
      orderBy('timestamp', 'desc'),
      limit(20)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const mentionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }));
      
      setMentions(mentionsData);
      
      // Count unread mentions
      const unreadCount = mentionsData.filter(m => !m.read).length;
      setUnreadMentionsCount(unreadCount);
      
      // Also add to general notifications (only if not already present)
      const mentionIds = new Set(notifications.map(n => 
        n.mentionId || 'not-a-mention-id'));
        
      const newNotificationsFromMentions = mentionsData
        .filter(m => !m.read && !mentionIds.has(m.id))
        .map(m => ({
          id: Date.now() + Math.random(),
          mentionId: m.id,
          message: m.message,
          timestamp: m.timestamp,
          read: false,
          type: 'mention',
          link: m.link
        }));
      
      if (newNotificationsFromMentions.length > 0) {
        setNotifications(prev => [...newNotificationsFromMentions, ...prev]);
      }
    });
    
    return () => unsubscribe();
  }, [currentUser]);
  
  // Function to mark all chat messages as read (callable from route components)
  const markAllChatAsRead = () => {
    const now = new Date();
    setLastReadChat(now);
    localStorage.setItem('lastReadChat', now.toISOString());
    setUnreadMessages(0);
  };

  // Function to mark a specific workspace's messages as read
  const markWorkspaceAsRead = (workspaceId) => {
    const now = new Date();
    setLastReadWorkspaces(prev => {
      const updated = { 
        ...prev, 
        [workspaceId]: now.toISOString()
      };
      localStorage.setItem('lastReadWorkspaces', JSON.stringify(updated));
      return updated;
    });
    
    setUnreadWorkspaceMessages(prev => ({
      ...prev,
      [workspaceId]: 0
    }));
  };

  // Track chat messages and update unread count
  useEffect(() => {
    if (!currentUser) return;
    
    // Get chat messages
    const q = query(
      collection(firestore, 'messages'),
      where('sender', '!=', currentUser.uid),
      orderBy('sender'),
      orderBy('timestamp', 'desc'),
      limitToLast(50)
    );
      
    const unsubscribe = onSnapshot(q, (snapshot) => {
      // Count messages newer than last read timestamp
      const unreadCount = snapshot.docs.filter(doc => {
        const data = doc.data();
        if (!data.timestamp) return false;
        
        const messageTime = data.timestamp.toDate ? 
          data.timestamp.toDate() : 
          new Date(data.timestamp);
          
        return messageTime > lastReadChat;
      }).length;
      
      setUnreadMessages(unreadCount);
    });
    
    return () => unsubscribe();
  }, [currentUser, lastReadChat]);

  // Track workspace messages and update unread counts for each workspace
  useEffect(() => {
    if (!currentUser) return;
    
    // Get all workspace messages
    const q = query(
      collection(firestore, 'workspace_messages'),
      where('userId', '!=', currentUser.uid),
      orderBy('userId'),
      orderBy('timestamp', 'desc'),
      limitToLast(100)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      // Group messages by workspace and count unread
      const workspaceCounts = {};
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!data.timestamp || !data.workspaceId) return;
        
        const workspaceId = data.workspaceId;
        const messageTime = data.timestamp.toDate ? 
          data.timestamp.toDate() : 
          new Date(data.timestamp);
        
        // Get last read time for this workspace
        const lastReadTime = lastReadWorkspaces[workspaceId] 
          ? new Date(lastReadWorkspaces[workspaceId])
          : new Date(0);
        
        // Count only if message is newer than last read time
        if (messageTime > lastReadTime) {
          workspaceCounts[workspaceId] = (workspaceCounts[workspaceId] || 0) + 1;
        }
      });
      
      setUnreadWorkspaceMessages(workspaceCounts);
    });
    
    return () => unsubscribe();
  }, [currentUser, lastReadWorkspaces]);

  // Update unread count when notifications change
  useEffect(() => {
    const unread = notifications.filter(n => !n.read).length;
    setUnreadCount(unread);
  }, [notifications]);

  // Add a new notification
  const addNotification = (message, type = 'info', username = null, action = null, taskName = null) => {
    const id = Date.now();
    let formattedMessage = message;
    
    // Format message if username and action are provided (for task operations)
    if (username && action) {
      if (taskName) {
        // Format: "{username} {action} {taskname}"
        formattedMessage = `${username} ${action} "${taskName}"`;
      } else {
        // Format: "{username} {action}"
        formattedMessage = `${username} ${action}`;
      }
    }
    
    const newNotification = {
      id,
      message: formattedMessage,
      timestamp: new Date(),
      read: false,
      type // 'success', 'error', 'info'
    };
    
    setNotifications(prevNotifications => [newNotification, ...prevNotifications]);
    setUnreadCount(prevCount => prevCount + 1);
    
    // Auto dismiss success notifications after timeout
    if (type !== 'error') {
      setTimeout(() => {
        setNotifications(prevNotifications => 
          prevNotifications.filter(notification => notification.id !== id)
        );
      }, 8000); // 8 seconds
    }
    
    return id;
  };

  // Add a mention notification
  const addMentionNotification = async (mentionedUserId, message, sourceType, sourceId, sourceName, link) => {
    if (!currentUser) return null;
    
    try {
      // Add to Firestore mentions collection
      const mentionRef = await addDoc(collection(firestore, 'mentions'), {
        userId: mentionedUserId,
        fromUserId: currentUser.uid,
        fromUserName: currentUser.displayName || currentUser.email || 'User',
        message,
        sourceType,
        sourceId,
        sourceName,
        link,
        read: false,
        timestamp: serverTimestamp()
      });
      
      return mentionRef.id;
    } catch (error) {
      console.error('Error adding mention notification:', error);
      return null;
    }
  };

  // Mark a notification as read
  const markAsRead = (id, e) => {
    if (e) e.stopPropagation(); // Prevent dismissing when marking as read
    
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  // Mark a mention as read
  const markMentionAsRead = async (mentionId) => {
    if (!mentionId) return;
    
    try {
      // Update in Firestore
      const mentionRef = doc(firestore, 'mentions', mentionId);
      await updateDoc(mentionRef, { read: true });
      
      // Update local state
      setMentions(prev => 
        prev.map(mention => 
          mention.id === mentionId ? { ...mention, read: true } : mention
        )
      );
      
      // Also update in general notifications if present
      setNotifications(prev => 
        prev.map(notification => 
          notification.mentionId === mentionId ? { ...notification, read: true } : notification
        )
      );
    } catch (error) {
      console.error('Error marking mention as read:', error);
    }
  };

  // Dismiss a single notification
  const dismissNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Mark all as read and close panel
  const closeNotifications = () => {
    // Mark all notifications as read
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
    
    // Close the panel
    setShowNotifications(false);
  };

  // Clear all notifications
  const clearAllNotifications = () => {
    setNotifications([]);
  };

  // Toggle notifications panel
  const toggleNotifications = () => {
    setShowNotifications(prev => !prev);
    if (showNotifications) {
      closeNotifications();
    }
  };

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        mentions,
        unreadCount,
        unreadMentionsCount,
        unreadMessages,
        unreadWorkspaceMessages,
        showNotifications,
        addNotification,
        addMentionNotification,
        markAsRead,
        markMentionAsRead,
        dismissNotification,
        closeNotifications,
        clearAllNotifications,
        toggleNotifications,
        setShowNotifications,
        markAllChatAsRead,
        markWorkspaceAsRead
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

// Export the custom hook for using notifications
export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

export default NotificationsContext;

// Hook to integrate notifications with router (to be used in route components)
export const useNotificationsRouteIntegration = () => {
  // Always call hooks at the top level
  const notificationsContext = useNotifications();
  
  // Safe access to the markAllChatAsRead function
  const markAllChatAsRead = notificationsContext?.markAllChatAsRead || (() => {
    console.warn('markAllChatAsRead not available');
  });
  
  // This function is meant to be called from inside a component that has router context
  const handleRouteChange = (pathname) => {
    try {
      // Check if we're on the chat page
      if (pathname === '/chat') {
        console.log('Detected chat route, marking messages as read');
        markAllChatAsRead();
      }
    } catch (error) {
      console.error('Error handling route change:', error);
    }
  };
  
  return { handleRouteChange };
}; 